import { useEffect, useState } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return (
    window.matchMedia && window.matchMedia("(display-mode: standalone)").matches
  ) || (navigator as any).standalone === true;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // If already installed, never show
    if (isStandalone()) return;

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setVisible(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS will never fire beforeinstallprompt — show helper for iOS
    if (isIOS()) {
      // Small delay to avoid clashing with initial page transitions
      const t = setTimeout(() => setShowIOSHelp(true), 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setVisible(false);
      } else {
        // Keep button available if dismissed
        setVisible(true);
      }
    } catch (_) {
      // Ignore errors, keep button visible
      setVisible(true);
    }
  };

  const closeIOS = () => setShowIOSHelp(false);

  // Render nothing if neither Android prompt nor iOS helper should show
  if (!visible && !showIOSHelp && !installed) return null;

  return (
    <div className="fixed z-50 bottom-[calc(var(--bottom-nav-height)+16px)] right-4 sm:right-6">
      {/* Android/Chromium install button */}
      {visible && (
        <div className="flex items-center gap-2 p-3 rounded-2xl shadow-lg backdrop-blur-premium bg-white/80 dark:bg-neutral-800/80 border border-neutral-200 dark:border-neutral-700">
          <button
            onClick={handleInstall}
            className="px-3 py-2 rounded-xl bg-sage-600 hover:bg-sage-700 text-white text-sm font-semibold shadow-sm"
          >
            Install App
          </button>
          <button
            onClick={() => setVisible(false)}
            aria-label="Dismiss"
            className="p-2 rounded-xl hover:bg-neutral-200 dark:hover:bg-neutral-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* iOS helper card */}
      {showIOSHelp && (
        <div className="mt-3 max-w-[94vw] w-[320px] p-3 rounded-2xl shadow-lg backdrop-blur-premium bg-white/90 dark:bg-neutral-800/90 border border-neutral-200 dark:border-neutral-700">
          <div className="text-sm font-semibold mb-1">Install on iPhone/iPad</div>
          <div className="text-xs text-neutral-700 dark:text-neutral-300">
            Open the browser Share menu and choose <span className="font-medium">Add to Home Screen</span>.
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={closeIOS} className="px-3 py-1.5 rounded-xl bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 text-neutral-900 dark:text-neutral-100 text-xs font-semibold">Got it</button>
          </div>
        </div>
      )}

      {/* installed toast */}
      {installed && (
        <div className="mt-3 px-3 py-2 rounded-xl bg-green-600 text-white text-xs shadow">Installed! Find FlightCents on your Home Screen.</div>
      )}
    </div>
  );
}

